import { ArrowRight, Boxes, Workflow } from "lucide-react";
import { Link } from "react-router-dom";

import { cardVariants } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * The two ways to start new work in a project: Submit a Tile, Submit a Flow.
 *
 * One component for both places that offer them, the Submit chooser and the
 * project page (fs#1894), so the two can never offer different choices. It
 * names no pipeline: every project gets the same two entry points, and which
 * tile or flow to run is picked on the next page.
 *
 * Props-only, no store or query coupling, so it can move to `flowstack-ui`
 * unchanged. The paths are the app's route table, not a new contract.
 */
export interface SubmitChoicesProps {
  /** Project slug — every submission route is project-scoped. */
  slug: string;
  className?: string;
}

/**
 * The two choices, as data (flowstack#1397).
 *
 * `label` is the link's ACCESSIBLE NAME, spelled out rather than left to the
 * accessible-name-from-contents computation over a paragraph of prose. The
 * operator's report was that a screen reader announced "link" here while every
 * other link on the page announced its destination; the fix that cannot regress
 * is an explicit name, and the visible title stays the first line of the card so
 * the two never disagree.
 */
const CHOICES = [
  {
    slugPath: "tile",
    Icon: Boxes,
    title: "Submit a Tile",
    /** Announced name — reads as a destination, not as a paragraph. */
    label: "Submit a Tile — one containerized step",
    tagline: "One containerized step.",
    body: (
      <>
        Pick from the catalog (e.g. <span className="font-mono">affdo.amber_topology</span>,{" "}
        <span className="font-mono">amberflow.binding_pose_refinement</span>) and supply its
        declared inputs and parameters. Best when you need exactly one computation — running a quick
        force-field assembly or refining a single pose.
      </>
    ),
    cost: "Fast: minutes to a few hours per tile.",
  },
  {
    slugPath: "flow",
    Icon: Workflow,
    title: "Submit a Flow",
    label: "Submit a Flow — a pipeline of tiles",
    tagline: "A pipeline of tiles, from a template.",
    body: (
      <>
        Pick a Flow template (e.g. the AFFDO torsion parameterization), supply its inputs, and set
        its parameters. The runner expands it into its steps, fans out where the template says to,
        and tracks every step as its own run.
      </>
    ),
    cost: "Slower: hours to days, depending on the template.",
  },
] as const;

export function SubmitChoices({ slug, className }: SubmitChoicesProps): JSX.Element {
  return (
    <ul className={cn("grid grid-cols-1 gap-4 sm:grid-cols-2", className)}>
      {CHOICES.map(({ slugPath, Icon, title, label, tagline, body, cost }) => (
        <li key={slugPath} className="flex">
          <Link
            to={`/projects/${slug}/submit/${slugPath}`}
            aria-label={label}
            // The card recipe arrives as a CLASS STRING so this element stays
            // an `<a>` with its own hover and focus treatment — the escape
            // hatch `card-adoption.test.tsx` names holders of. It used to be a
            // `<Link>` WRAPPING a `<Card>`, which is a third pattern: the
            // surface and the control were two elements, so the focus ring
            // sat on one and the border on the other.
            className={cn(
              cardVariants({ elevation: "raised" }),
              "group flex flex-1 flex-col gap-2 p-card outline-none transition-colors",
              "hover:border-border-strong hover:bg-surface-2",
              "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            )}
          >
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
              <h2 className="font-display text-base font-semibold tracking-tight text-foreground">
                {title}
              </h2>
            </div>
            <p className="text-sm text-muted-foreground">{tagline}</p>
            <p className="text-sm text-muted-foreground">{body}</p>
            <p className="text-xs text-muted-foreground">{cost}</p>
            {/* The affordance half of flowstack#1397. An accessible name
                closes the WCAG failure; it does not make two paragraphs of
                prose look like choices. `Choose →` is the same control
                vocabulary the Flow picker's cards carry, so a reader who has
                seen one recognises the other. */}
            <span className="mt-auto inline-flex items-center gap-1 pt-1 text-[11px] font-medium text-muted-foreground group-hover:text-foreground">
              Choose
              <ArrowRight className="h-3 w-3" aria-hidden />
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
